import "./styles.css";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import {
  AXIS_MAX,
  Action,
  Buff,
  Button,
  CROWD,
  DUEL_RULES,
  MatchPhase,
  GameMode,
  Movement,
  PickupKind,
  PropStatus,
  aimTarget,
  areAllies,
  attackDef,
  effectiveAttack,
  canBeHit,
  groundHeightAt,
  inHitbox,
  isActiveTick,
  quantizeAxis,
  type ArenaDefinition,
  type CharacterManifest,
  type ChatLine,
  type CombatEvent,
  type PickupState,
  type PlayerState,
  type ProjectileState,
} from "@arena/shared";
import { CloseCode } from "@colyseus/sdk";
import { RoomCloseCode, type Notice } from "@arena/shared";
import { DEFAULT_ARENA_ID, getArena, inMuseum, museumObjectAt, withMuseum } from "@arena/arena-data";
import { CHARACTERS, DEFAULT_CHARACTER_ID, MUSEUM_CHAMPIONS, charmedCommand, findCharacter, kitOf, summonOf, voicesCommand } from "@arena/character-data";
import {
  clearResumeToken,
  inviteLink,
  loadResumeToken,
  loadSettings,
  saveName,
  saveDiorama,
  saveResolution,
  saveResumeToken,
  setRoomInUrl,
} from "./config.ts";
import { Renderer } from "./game/renderer.ts";
import { ArenaView } from "./game/arenaView.ts";
import { MuseumView } from "./game/museumView.ts";
import { PlayerView } from "./game/playerView.ts";
import { SpriteFighter, preloadProtoSheets } from "./game/spriteFighter.ts";
import { setupHd2d, type Hd2d } from "./game/hd2d.ts";
import { stage, type Staged } from "./game/staging.ts";
import { MoveEffects, effectFor } from "./game/moveEffects.ts";
import { PistolVisuals, enemies } from "./game/pistolVisuals.ts";
import { AimGuide } from "./game/aimGuide.ts";
import { DirectionTargeting } from "./input/directionTargeting.ts";
import { BarricadesView } from "./game/barricadeView.ts";
import { MussoliniScene } from "./game/mussoliniScene.ts";
import { ProjectileView } from "./game/projectileView.ts";
import { SummonView, preloadSummonArt } from "./game/summonView.ts";
import { CombatSpriteView } from "./game/combatSpriteView.ts";
import { SaltZoneView } from "./game/saltZoneView.ts";
import { MilkBathView } from "./game/cleopatraViews.ts";
import { BriocheMineView } from "./game/antoniettaViews.ts";
import { BannerView } from "./game/giovannaViews.ts";
import { statusMarks } from "./ui/statusMarks.ts";
import {KafkaWaterFx} from './game/kafkaWater.ts';
import {TentacleView} from './game/tentacleView.ts';
import { allVisuals, visualsOf, type ChampionFx, type DrawnFighter, type ShotView } from "./champions/visuals.ts";
import {SwarmZoneView,AnchorView} from './game/abilityAreas.ts';
import { ColosseoView } from "./game/colosseoView.ts";
import { InfernoView } from "./game/infernoView.ts";
import { PropsView } from "./game/propsView.ts";
import { PickupView } from "./game/pickupView.ts";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CameraRig } from "./game/cameraRig.ts";
import { Effects } from "./game/effects.ts";
import { TEAM_NAMES, fighterCss } from "./palette.ts";
import { InputController } from "./input/controller.ts";
import {VirgilioTargeting} from './input/virgilioTargeting.ts';
import { GameSession, serverEndpoint } from "./net/session.ts";
import { GoogleAuth } from "./net/googleAuth.ts";
import { reloadOutdated, watchForUpdates } from "./net/freshness.ts";
import { Hud } from "./ui/hud.ts";
import { MatchHud, type ResultRow, type ScoreRow } from "./ui/matchHud.ts";
import { HomeScreen } from "./ui/home.ts";
import { LobbyPanel, type LobbyView } from "./ui/lobby.ts";
import { MuseumHud } from "./ui/museumHud.ts";
import { AbilityButtons, abilitySlot } from "./ui/abilities.ts";
import { GrabButton } from "./ui/grabButton.ts";
import { HealthBar } from "./ui/healthBar.ts";
import { MatchIntro } from "./ui/matchIntro.ts";
import { SpectatorPanel } from "./ui/spectator.ts";
import {GameAudio} from './audio/gameAudio.ts';
import {audioControls} from './ui/audioControls.ts';

const settings = loadSettings();
/** The room's map: the host picks it in the curator's register and the scene follows (D49, `useArena`). */
let arena = getArena(DEFAULT_ARENA_ID);
const gameAudio=new GameAudio(arena);
const audioPanel=audioControls(gameAudio.mixer);
const audioRight=new Vector3();

const canvas = document.getElementById("game") as HTMLCanvasElement;
const labels = document.getElementById("labels")!;
const renderer = new Renderer(canvas, settings.resolution);
const look2d = settings.look === "2d";
if(look2d){preloadProtoSheets(CHARACTERS.map(c=>c.id));preloadSummonArt(renderer.scene);}
// The museum where a room waits between matches (D49): a wing of the same world, 200 m away. It stays
// when the map changes, so it is built before (and apart from) the arena's views.
const museumView = new MuseumView(renderer.scene, labels, MUSEUM_CHAMPIONS);
/** HD-2D prototype: a lower camera, closer, like a diorama seen from the side. */
const cameraOf = (a: ArenaDefinition): ArenaDefinition => look2d ? { ...a, camera: { ...a.camera, pitch: 0.68, distance: 12.5 } } : a;
const rig = new CameraRig(renderer.camera, cameraOf(arena));
// The arena's views, rebuilt on a change of map. Themed maps have their own look in HD-2D (D38, D39):
// the boxes stay for collisions only.
let world = withMuseum(arena);
let arenaView!: ArenaView;
let colosseo: ColosseoView | null = null;
let inferno: InfernoView | null = null;
let propsView: PropsView | null = null;
let fireIds: string[] = [];
let hd2d: Hd2d | null = null;
let staged: Staged | null = null;
function buildArena(): void {
  staged = stage(renderer.scene, () => {
    const themed = look2d && arena.theme !== undefined;
    arenaView = new ArenaView(renderer.scene, arena, look2d ? "hd2d" : "greybox", !themed);
    colosseo = themed && arena.theme === "colosseo" ? new ColosseoView(renderer.scene, arena) : null;
    inferno = themed && arena.theme === "inferno" ? new InfernoView(renderer.scene, arena) : null;
    propsView = themed && arena.props ? new PropsView(renderer.scene, arena.props) : null;
    fireIds = propsView?.fireIds ?? [];
    hd2d = look2d
      ? setupHd2d(
          renderer.scene,
          renderer.camera,
          inferno
            ? { ...inferno.hd2dWorld({ casters: propsView?.casters ?? [], torches: propsView?.firePoints ?? [] }), fireDrawn: true }
            : colosseo
              ? colosseo.hd2dWorld()
              : arenaView.hd2dWorld(),
        )
      : null;
  });
  world = withMuseum(arena);
}
buildArena();
const overviewButton=document.getElementById('training-overview')!;
overviewButton.addEventListener('click',()=>{
  rig.overview=!rig.overview;
  overviewButton.setAttribute('aria-pressed',String(rig.overview));
  overviewButton.textContent=rig.overview?'Segui personaggio':'Panoramica';
});
const dioramaButton=document.getElementById('diorama-button')!;
dioramaButton.hidden=!hd2d;
function applyDiorama():void {
  hd2d?.setDiorama(settings.diorama);
  dioramaButton.setAttribute('aria-pressed',String(settings.diorama));
  dioramaButton.textContent=settings.diorama?'Diorama ✓':'Diorama';
}
applyDiorama();
dioramaButton.addEventListener('click',()=>{
  settings.diorama=!settings.diorama;saveDiorama(settings.diorama);applyDiorama();
});

/**
 * The host picked another map in the register (D49): the old arena's views
 * leave the scene whole, the new one's are built, and everything that walks
 * or sounds on the map follows.
 */
function useArena(id: string): void {
  if (!id || id === arena.id) return;
  hd2d?.dispose();
  staged?.dispose();
  arena = getArena(id);
  buildArena();
  applyDiorama();
  rig.setArena(cameraOf(arena));
  gameAudio.setArena(arena);
  session?.setArena(arena);
  for (const f of fighters.values()) f.view.setArena(world);
}
const effects = new Effects(renderer.scene, labels);
if (settings.debug) (globalThis as { __scene?: unknown }).__scene = renderer.scene;
const moveEffects = new MoveEffects(renderer.scene);
const kafkaWater=new KafkaWaterFx(renderer.scene);
const swarmZones=new Map<string,SwarmZoneView>();
const anchorViews=new Map<string,AnchorView>();
const pistolVisuals = new PistolVisuals(renderer.scene);
/** Where the next move goes, and the enemy the pistol would lock onto (D43). */
const aimGuide = new AimGuide(renderer.scene);
/** Where the floor arrow points now: an aimed S2 sent "towards the arrow" goes there. */
let guideAim = { x: 0, z: 1 };
const mussoliniScene=new MussoliniScene(renderer.scene,moveEffects);
const barricadesView=new BarricadesView(renderer.scene);
const saltZones = new Map<string, SaltZoneView>();
/** Cleopatra's baths of milk on the floor (D45). */
const milkBaths = new Map<string, MilkBathView>();
const briocheMines = new Map<string, BriocheMineView>();
const banners = new Map<string, BannerView>();
const hud = new Hud(settings.resolution);
const matchHud = new MatchHud();
const matchIntro = new MatchIntro();
/** Arena KO: eliminated, the player watches the rest of the match or leaves (D42). */
const spectator = new SpectatorPanel();
spectator.onLeave = () => leaveRoom();
const home = new HomeScreen(CHARACTERS);
const lobby = new LobbyPanel(CHARACTERS, audioPanel);
const museumHud = new MuseumHud(MUSEUM_CHAMPIONS);
const museumProfile = document.getElementById("museum-profile")!;
const museumProfileName = document.getElementById("museum-profile-name")!;
const museumProfileInitial = document.getElementById("museum-profile-initial")!;
const museumProfileLevel = document.getElementById("museum-profile-level")!;
const museumProfilePhoto = document.getElementById("museum-profile-photo") as HTMLImageElement;
museumProfilePhoto.addEventListener("error", () => { museumProfilePhoto.hidden = true; });
museumHud.onPick = (id) => session?.chooseCharacter(id);
museumHud.onLook = (id) => {
  if (id === "cleopatra") void gameAudio.mixer.cleopatraLine("select");
};
museumHud.onDesk = () => lobby.openRegister();
museumHud.onTrain = (on) => session?.train(on);
museumHud.onSay = (text) => session?.say(text);
museumHud.onStart = () => session?.startMatch();
const abilities = new AbilityButtons();
const grabButton = new GrabButton();
const toast = document.getElementById("toast")!;
const crowdMeter = document.getElementById("match-crowd")!;
const crowdFill = crowdMeter.querySelector<HTMLElement>("i")!;

const controls = new InputController(
  document.getElementById("stick-zone")!,
  document.getElementById("stick-base")!,
  document.getElementById("stick-knob")!,
  document.getElementById("look-zone")!,
  document.querySelectorAll<HTMLElement>("[data-button]"),
);
controls.onLook = (dx, dy) => rig.drag(dx, dy);

hud.onResolutionChange = (profile) => {
  renderer.setResolution(profile);
  saveResolution(profile);
};
hud.onRetry = () => location.reload();

interface Fighter {
  id: string;
  state: PlayerState;
  view: PlayerView | SpriteFighter;
  isLocal: boolean;
  health: HealthBar;
}

const fighters = new Map<string, Fighter>();
const creatureViews=new Map<string,{view:SpriteFighter|PlayerView;health:HealthBar}>();
const tentacleViews=new Map<string,TentacleView>();
const cloneViews = new Map<string, SpriteFighter | PlayerView>();
const cloneHealthBars = new Map<string, HealthBar>();
const drawnCloneOwners = new Set<string>();
const summons: { view: SummonView; x: number; y: number; z: number; fx: number; fz: number; age: number }[] = [];
const flourishes: { view: CombatSpriteView; x: number; y: number; z: number; fx: number; fz: number; age: number }[] = [];
/** A thrown object of the map in flight: it spins. */
class ThrownView {
  private readonly mesh: Mesh;
  private time = 0;
  constructor(mesh: Mesh) {
    this.mesh = mesh;
  }
  update(x: number, y: number, z: number, _fx: number, _fz: number, dt: number): void {
    this.time += dt;
    this.mesh.position.set(x, y + 0.6, z);
    this.mesh.rotation.z = this.time * 14;
  }
  dispose(): void {
    this.mesh.dispose(false, true);
  }
}

const projectiles = new Map<string, { state: ProjectileState; view: ShotView }>();
const pickups = new Map<string, PickupView>();
/** Glow under the fighters that drank from the amphora. */
const buffRings = new Map<string, Mesh>();
const buffMaterial = new StandardMaterial("buff", renderer.scene);
buffMaterial.disableLighting = true;
buffMaterial.emissiveColor = Color3.FromHexString("#ff5a2a");
let session: GameSession | null = null;

/** What the champions' visuals may use (D40). */
const championFx: ChampionFx = {
  scene: renderer.scene,
  look2d,
  effects,
  moveEffects,
  // The map's views change with the map (D49): read them when used.
  get propsView() { return propsView; },
  get hd2d() { return hd2d; },
  rig,
  matchHud,
  kafkaWater,
  localId: () => session?.sessionId,
  isLocal: (id) => fighters.get(id)?.isLocal ?? false,
  fighter: (id) => fighters.get(id),
  summon: (view, event) => summons.push({ view, ...event, age: 0 }),
  flourish: (view, x, y, z, fx, fz) => flourishes.push({ view, x, y, z, fx, fz, age: 0 }),
};
const virgilioAim=new VirgilioTargeting(canvas,renderer.camera,controls,()=>{
  if(!session || (session.state.phase!==MatchPhase.Playing && session.state.phase!==MatchPhase.Lobby))return null;
  const p=session.localPredicted;
  const state=session.state.players.get(session.sessionId);
  // Only champions whose S1 is aimed have a label (D40).
  const label=state?visualsOf(state.characterId)?.aimLabel?.(state)??null:null;
  if(!p || !state || !label || p.knockedOut || p.matchFrozen || p.anchorTicks>0)return null;
  return {arena:session.liveArena(),source:p,label,ready:p.cooldown1===0 && p.hitstun===0 && p.action===Action.None};
});
/** Dante's devil in the direction the player picks (D44); built after Virgilio's, whose press handler it wraps. */
const devilAim=new DirectionTargeting(canvas,renderer.camera,controls,()=>{
  if(!session || (session.state.phase!==MatchPhase.Playing && session.state.phase!==MatchPhase.Lobby))return null;
  const p=session.localPredicted;
  const state=session.state.players.get(session.sessionId);
  const label=state?visualsOf(state.characterId)?.aimDirection?.(state)??null:null;
  const move=state?kitOf(state.characterId,state.metamorphTicks).moves.special2:null;
  if(!p || !state || !label || !move?.projectile || p.knockedOut || p.matchFrozen || p.anchorTicks>0)return null;
  return {arena:session.liveArena(),source:p,label,move,guide:guideAim,ready:p.cooldown2===0 && p.hitstun===0 && p.action===Action.None};
});

/** The manifest of a champion id; the default champion while the id is unknown (or still empty). */
function championOf(id: string): CharacterManifest {
  return findCharacter(id) ?? findCharacter(DEFAULT_CHARACTER_ID)!;
}

// ---------------------------------------------------------------------------
// Room flow: home → create / join by code → lobby ⇄ match → leave → home
// ---------------------------------------------------------------------------

const sessionEvents = {
  playerAdded(sessionId: string, state: PlayerState, isLocal: boolean) {
    const champion = championOf(state.characterId);
    const View = look2d ? SpriteFighter : PlayerView;
    const view = new View(renderer.scene, world, state.name, state.color, isLocal, labels, champion, kitOf(champion.id));
    fighters.set(sessionId, { id: sessionId, state, view, isLocal, health: new HealthBar(view.label) });
  },
  playerRemoved(sessionId: string) {
    fighters.get(sessionId)?.view.dispose();
    fighters.delete(sessionId);
    buffRings.get(sessionId)?.dispose();
    buffRings.delete(sessionId);
  },
  projectileAdded(id: string, state: ProjectileState) {
    const team = session?.state.players.get(state.owner)?.team ?? 0;
    // A thrown object flies as itself; an ability's shot as the shooter's energy ball.
    const thrown = state.prop && propsView ? propsView.flying(state.prop) : null;
    // An ability's shot drawn the champion's own way (D40), else the shooter's energy ball.
    const shooter = thrown ? undefined : session?.state.players.get(state.owner);
    const own = shooter ? visualsOf(shooter.characterId)?.shotView?.(championFx, state) ?? null : null;
    const view = thrown ? new ThrownView(thrown) : own ?? new ProjectileView(renderer.scene, state.radius, fighterCss(state.color, team));
    projectiles.set(id, { state, view });
  },
  projectileRemoved(id: string) {
    projectiles.get(id)?.view.dispose();
    projectiles.delete(id);
  },
  pickupAdded(id: string, state: PickupState) {
    pickups.set(id, new PickupView(renderer.scene, state));
  },
  pickupRemoved(id: string) {
    pickups.get(id)?.dispose();
    pickups.delete(id);
  },
  combat: onCombat,
  notice: (n: Notice) => showToast(n.message),
  chat: (line: ChatLine) => heardChat(line),
  dropped: () => hud.showStatus("Connessione persa, riconnessione in corso…"),
  reconnected: () => hud.hideStatus(),
  left: (code: number) => leaveRoom(leaveMessage(code)),
};

/** Sign-in with Google, when the server requires it. */
let googleAuth: GoogleAuth | null = null;

async function boot(): Promise<void> {
  if (settings.debug) await enableNetworkDebug();
  // Sign-in with Google, when the server asks for it: the room buttons wait for it.
  void GoogleAuth.fromServer(serverEndpoint()).then((auth) => {
    if (!auth) return;
    googleAuth = auth;
    home.useAuth(auth);
  });
  // A reload inside a room takes back the same seat (the server keeps it ~20 s).
  const token = loadResumeToken();
  if (token) {
    hud.showStatus("Rientro nella stanza…");
    try {
      enterRoom(await GameSession.resume(token, arena, sessionEvents));
      return;
    } catch {
      clearResumeToken();
    }
    hud.hideStatus();
  }
  home.show(settings.name, settings.roomCode);
}

home.onCreate = name => void join(name, () => GameSession.create(name, arena, sessionEvents));
home.onJoin = (code, name) => void join(name, () => GameSession.joinByCode(code, name, arena, sessionEvents));
home.onSolo = name => void join(name, () => GameSession.create(name, arena, sessionEvents), true);

/** `solo`: a sparring bot joins, training in the arena; the player starts in the museum like everyone (D49). */
async function join(name: string, open: () => Promise<GameSession>, solo = false): Promise<void> {
  saveName(name);
  settings.name = name;
  home.setBusy(true);
  try {
    const opened = await open();
    enterRoom(opened);
    if (solo) {
      opened.addBot();
    }
  } catch (error) {
    console.error(error);
    // The server is newer than this page: the reload brings the matching one.
    if ((error as { code?: number })?.code === 426 && reloadOutdated(showUpdating)) return;
    home.setBusy(false);
    // Refused sign-in (expired or revoked): Google's button again.
    if ((error as { code?: number })?.code === 401) googleAuth?.signOut();
    home.showError(joinErrorMessage(error));
  }
}

function enterRoom(s: GameSession): void {
  session = s;
  saveResumeToken(s.reconnectionToken);
  setRoomInUrl(s.room.roomId);
  home.hide();
  hud.hideStatus();
}

/** Back to the home screen, with an explanation when we did not choose to leave. */
function leaveRoom(message = ""): void {
  gameAudio.reset();
  const code = session?.room.roomId ?? "";
  const leaving = session;
  session = null;
  void leaving?.leave();
  for (const fighter of fighters.values()) fighter.view.dispose();
  fighters.clear();
  for(const c of creatureViews.values())c.view.dispose();creatureViews.clear();
  for(const t of tentacleViews.values())t.dispose();tentacleViews.clear();
  for(const view of cloneViews.values())view.dispose();cloneViews.clear();
  cloneHealthBars.clear();drawnCloneOwners.clear();
  for (const shot of projectiles.values()) shot.view.dispose();
  projectiles.clear();
  for (const summon of summons) summon.view.dispose();
  summons.length=0;
  for (const flourish of flourishes) flourish.view.dispose();
  flourishes.length=0;
  moveEffects.clear();
  pistolVisuals.clear();
  mussoliniScene.clear();
  barricadesView.clear();
  for (const zone of saltZones.values()) zone.dispose();
  saltZones.clear();
  for(const zone of swarmZones.values())zone.dispose();swarmZones.clear();
  for (const bath of milkBaths.values()) bath.dispose();
  milkBaths.clear();
  for (const mine of briocheMines.values()) mine.dispose();
  briocheMines.clear();
  for (const banner of banners.values()) banner.dispose();
  banners.clear();
  for(const view of anchorViews.values())view.dispose();anchorViews.clear();kafkaWater.clear();
  for (const pickup of pickups.values()) pickup.dispose();
  pickups.clear();
  for (const ring of buffRings.values()) ring.dispose();
  buffRings.clear();
  clearResumeToken();
  setRoomInUrl(null);
  matchHud.clear();
  matchIntro.clear();
  spectator.update(null);
  lobby.reset();
  hud.hideStatus();
  home.show(settings.name, message ? code : "", message);
}

lobby.onStart = () => session?.startMatch();
lobby.onSettings = (patch) => session?.changeSettings(patch);
lobby.onKick = (id) => session?.kick(id);
lobby.onTeam = (team) => session?.chooseTeam(team);
lobby.onShuffle = () => session?.shuffleTeams();
lobby.onAddBot = () => session?.addBot();
lobby.onLeave = () => leaveRoom();
lobby.onShare = () => void shareInvite();
lobby.onMap = (id) => session?.chooseMap(id);

async function shareInvite(): Promise<void> {
  if (!session) return;
  const code = session.room.roomId;
  const url = inviteLink(code);
  const text = `Entra nella mia stanza di Arena Crossover: codice ${code}`;
  try {
    if (navigator.share) {
      await navigator.share({ title: "Arena Crossover", text, url });
      return;
    }
    await navigator.clipboard.writeText(`${text}\n${url}`);
    showToast("Link copiato: incollalo ai tuoi amici.");
  } catch {
    showToast(`Codice stanza: ${code}`);
  }
}

let toastTimer = 0;
function showToast(message: string): void {
  toast.textContent = message;
  toast.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (toast.hidden = true), 3200);
}

function leaveMessage(code: number): string {
  switch (code) {
    case CloseCode.CONSENTED:
      return "";
    case RoomCloseCode.Kicked:
      return "Sei stato allontanato dalla stanza.";
    case RoomCloseCode.Expired:
      return "La stanza è scaduta per inattività: creane una nuova.";
    case CloseCode.SERVER_SHUTDOWN:
      return "Il server si sta aggiornando: rientra tra qualche secondo.";
    default:
      return "Connessione persa. Puoi rientrare con lo stesso codice.";
  }
}

/** Server and network errors → something a player can act on. */
function joinErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/not found|disposed/i.test(message)) return "Stanza non trovata: controlla il codice.";
  if (/locked|full/i.test(message)) return "La stanza è piena o la partita è già in corso: riprova tra poco.";
  if (/fetch|network|ECONNREFUSED|Failed/i.test(message)) return "Server non raggiungibile: controlla la connessione.";
  return message || "Impossibile entrare nella stanza.";
}

/** `?debug`, `?lag=ms`, `?jitter=ms`: Colyseus debug panel + network simulator. */
async function enableNetworkDebug(): Promise<void> {
  await import("@colyseus/sdk/debug");
  const net = (globalThis as { __net?: (delayMs: number, jitterMs: number) => void }).__net;
  if (settings.lagMs > 0 || settings.jitterMs > 0) net?.(settings.lagMs, settings.jitterMs);
}

// ---------------------------------------------------------------------------
// Combat feedback
// ---------------------------------------------------------------------------

/** A server hit confirming a spark the client already showed within this time is not shown twice. */
const PREDICTED_HIT_MS = 600;
const spot = new Vector3();

/** Sparks of the local attacks are shown at once; the server's event only confirms them. */
const predictedHits = new Map<string, number>();
const predicting = { action: 0, tick: 0, hit: new Set<string>() };

/**
 * Anticipates the local fighter's hits for feedback only (GDD §3.6): same
 * hitbox test as the server, against the targets as they are drawn here —
 * which is also what the server rewinds to. Scores, meters and hitstun stay
 * authoritative.
 */
function predictLocalHits(local: PlayerState, characterId: string, team: number, now: number): void {
  if (local.action !== predicting.action || local.actionTick < predicting.tick) predicting.hit.clear();
  predicting.action = local.action;
  predicting.tick = local.actionTick;

  // Summons, channels and aimed casts land elsewhere or later: the server shows them (D40).
  const own = session?.state.players.get(session.sessionId);
  if(own && visualsOf(characterId)?.unpredicted?.(own,local.action))return;
  const def = effectiveAttack(kitOf(characterId,session?.state.players.get(session.sessionId)?.metamorphTicks).table, local);
  if (!def || def.id === Action.SuperSlam || def.projectile || !isActiveTick(def, local.actionTick)) return;
  for (const fighter of fighters.values()) {
    if (fighter.isLocal || predicting.hit.has(fighter.id) || areAllies(team, fighter.state.team)) continue;
    if (fighter.state.knockedOut || fighter.state.spawnShieldTicks > 0) continue;
    if (!canBeHit(fighter.state, kitOf(fighter.state.characterId,fighter.state.metamorphTicks).table, def)) continue;
    if (def.landingAttack && !local.grounded) continue;
    if (!inHitbox(local, def, fighter.view.position)) continue;
    predicting.hit.add(fighter.id);
    predictedHits.set(fighter.id, now);
    effects.spark(fighter.view.chest(spot), def.lift > 0);
  }
}

function onCombat(event: CombatEvent): void {
  if(session?.state.phase===MatchPhase.Lobby && lobby.open)return;
  gameAudio.combat(event);
  const localId = session?.sessionId;
  const now = performance.now();
  switch (event.type) {
    case "photo": {
      mussoliniScene.flash(event.caster);
      const view=fighters.get(event.caster)?.view;
      if(view instanceof SpriteFighter)view.illuminate();
      if(event.gain>0)effects.popup(`+${event.gain} ★`,new Vector3(event.x,event.y+3.6,event.z),"info");
      break;
    }
    case "grab": {
      const target=fighters.get(event.target);
      if(target)effects.popup("CAMBIO DI PROGRAMMA",target.view.chest(spot).addInPlaceFromFloats(0,0.7,0),"combo");
      break;
    }
    case "airstrike":
      matchHud.flash("INCURSIONE AEREA!",1500);
      break;
    case "brioche": {
      // A puff of powdered sugar and cream: lighter than a bomb, the same push.
      const at=new Vector3(event.x,event.y,event.z);
      moveEffects.burst("antonietta-sugar",event.x,event.y+0.7,event.z,event.radius);
      effects.shockwave(at,event.radius);
      propsView?.disturb(event.x,event.z,event.radius);
      if(session?.localPredicted && Vector3.DistanceSquared(at,new Vector3(session.localPredicted.x,session.localPredicted.y,session.localPredicted.z))<36)rig.shake(0.1);
      break;
    }
    case "bomb": {
      const at=new Vector3(event.x,event.y,event.z);
      moveEffects.burst("mussolini-explosion",event.x,event.y+0.8,event.z,event.radius);
      effects.shockwave(at,event.radius);
      propsView?.disturb(event.x,event.z,event.radius+1);
      if(session?.localPredicted && Vector3.DistanceSquared(at,new Vector3(session.localPredicted.x,session.localPredicted.y,session.localPredicted.z))<100)rig.shake(0.18);
      hd2d?.pulse(0.2);
      break;
    }
    case 'restore': {
      const caster=fighters.get(event.caster);
      if(caster && event.amount>0)effects.popup('+'+event.amount+(event.resource==='hp'?' HP':' SUPREMA'),caster.view.chest(spot),'info');
      break;
    }
    case 'summonHit': {
      const c=creatureViews.get(event.id);if(c){if(c.view instanceof SpriteFighter)c.view.impact('target',event.dead);effects.spark(c.view.position.add(new Vector3(0,.8,0)),event.dead);}break;
    }
    case 'tentacleHit': {
      const at=new Vector3(event.x+event.fx*event.length/2,event.y+.08,event.z+event.fz*event.length/2);
      effects.spark(at,true);propsView?.disturb(at.x,at.z,2);
      if(event.caster===localId)rig.shake(.13);break;
    }
    case "damage": {
      const target = fighters.get(event.target);
      if (target) effects.popup(`−${event.amount}`,target.view.chest(spot).addInPlaceFromFloats(0,0.5,0),"info");
      break;
    }
    case "ko": {
      effects.popup("KO!",new Vector3(event.x,event.y+1.8,event.z),"score");
      effects.spark(new Vector3(event.x,event.y+0.7,event.z),true);
      if (event.attacker === localId) matchHud.flash(session?.state.phase === MatchPhase.Playing ? "KO! +1" : "KO DI ALLENAMENTO");
      if (event.target === localId) rig.shake(0.3);
      if (event.target === localId && session?.state.phase === MatchPhase.Playing) spectator.knockedOutBy(event.attacker);
      break;
    }
    case "respawn":
      if (event.target === localId) matchHud.flash("DI NUOVO IN ARENA",700);
      break;
    case "move": {
      const caster = fighters.get(event.caster);
      if (!look2d || !caster) break;
      visualsOf(caster.state.characterId)?.onMove?.(championFx, caster, event);
      break;
    }
    case "counter": {
      if (look2d) moveEffects.ground("gandhi-counter",event.x,event.y,event.z,1.6*1.3);
      propsView?.disturb(event.x,event.z,2.6);
      hd2d?.pulse(0.5);
      effects.popup("ORA BASTA!",new Vector3(event.x,event.y+2,event.z),"combo");
      if (event.caster === localId) rig.shake(0.2);
      break;
    }
    case "purify":
      if (look2d) moveEffects.burst("gandhi-purify",event.x,event.y,event.z,0.55);
      break;
    case "bounce":
      effects.spark(new Vector3(event.x,event.y+0.2,event.z),true);
      if (event.caster === localId) rig.shake(0.1);
      break;
    case "hit": {
      const target = fighters.get(event.target);
      if (!target) return;
      const shown = event.attacker === localId && now - (predictedHits.get(event.target) ?? -Infinity) < PREDICTED_HIT_MS;
      predictedHits.delete(event.target);
      const attacker = fighters.get(event.attacker);
      const strong = attacker ? (attackDef(kitOf(attacker.state.characterId,attacker.state.metamorphTicks).table, event.action)?.lift ?? 0) : 0;
      if (!shown) effects.spark(target.view.chest(spot), strong > 0);
      if (event.combo >= 2) effects.popup(`x${event.combo}`, target.view.chest(spot).addInPlaceFromFloats(0, 0.7, 0), "combo");
      if (event.target === localId) rig.shake(strong > 0 ? 0.25 : 0.1);
      else if (event.attacker === localId && strong > 0) rig.shake(0.12);
      // HD-2D: both fighters hold their pose for a moment, the blow lands with weight.
      if (attacker?.view instanceof SpriteFighter) attacker.view.impact("attacker", strong > 0);
      if (target.view instanceof SpriteFighter) target.view.impact("target", strong > 0);
      break;
    }
    case "slam": {
      const caster = fighters.get(event.caster);
      // Decoys, tentacles, a transformation: Super Slams drawn by their champion (D40).
      const visuals = caster ? visualsOf(caster.state.characterId) : undefined;
      if (caster && visuals?.onSlam?.(championFx, caster, event)) break;
      if (caster && !visuals?.noSlamArea) {
        // Drawn with the caster's own shape (a circle or a cone).
        const def = kitOf(caster.state.characterId).moves.superSlam;
        const reach = def.range + Movement.radius;
        const at = caster.view.position;
        propsView?.disturb(at.x, at.z, reach + 2);
        hd2d?.pulse(visuals?.slamPulse ?? 0.65);
        // The champion's own drawn effect (HD-2D) when it has one and it is a full circle, else the generic shockwave.
        effects.shockwave(at, reach, def.arcDeg, caster.state.fx, caster.state.fz);
      }
      for (const id of event.targets) {
        const target = fighters.get(id);
        if (target) effects.spark(target.view.chest(spot), true);
      }
      if (caster && event.points > 0) {
        effects.popup(`+${event.points}`, caster.view.chest(spot).addInPlaceFromFloats(0, 1.4, 0), "score");
      }
      if (event.caster === localId) {
        // In training (lobby) a hit scores nothing but is not "empty".
        const ko = session?.state.settings.gameMode === GameMode.KO;
        if (!ko || event.targets.length === 0) matchHud.flash(event.points > 0 ? `SUPER SLAM! +${event.points}` : event.targets.length > 0 ? "SUPER SLAM!" : "Super Slam a vuoto");
      }
      if (event.caster === localId || (localId !== undefined && event.targets.includes(localId))) rig.shake(0.45);
      break;
    }
    case "slamCancel": {
      moveEffects.cancel(`slam-${event.id}`);
      const caster = fighters.get(event.caster);
      if (caster) effects.popup("Super Slam interrotta", caster.view.chest(spot).addInPlaceFromFloats(0, 1.4, 0), "info");
      break;
    }
    case "slamStart": {
      const caster=fighters.get(event.caster);
      if (look2d && caster && !visualsOf(caster.state.characterId)?.noSlamWindup) {
        const fx=effectFor(caster.state.characterId,"super");
        const at=caster.view.position;
        if(fx)moveEffects.ground(fx,at.x,at.y,at.z,kitOf(caster.state.characterId).moves.superSlam.range+Movement.radius,`slam-${event.id}`);
      }
      break;
    }
    case "lever": {
      const who = fighters.get(event.who);
      if (who) effects.popup("LEVA!", who.view.chest(spot).addInPlaceFromFloats(0, 1.2, 0), "info");
      matchHud.flash("BOTOLE!", 1400);
      colosseo?.cheer(0.6);
      break;
    }
    case "propBreak":
      propsView?.shatter(event.id, event.x, event.y, event.z);
      inferno?.react(event.x,event.z);
      break;
    case "propHit":
      propsView?.hit(event.id);
      break;
    case "cheer":
      colosseo?.cheer(1);
      matchHud.flash("IL PUBBLICO LANCIA OGGETTI!", 1400);
      break;
    case "pickup": {
      const who = fighters.get(event.who);
      const apple = event.kind === PickupKind.Apple;
      const text = apple ? (session?.state.phase === MatchPhase.Playing ? "MELA: SUPER CARICO!" : "MELA!") : "ANFORA: FORZA!";
      if (who) effects.popup(text, who.view.chest(spot).addInPlaceFromFloats(0, 1.2, 0), "score");
      if (event.who === localId) matchHud.flash(text, 1200);
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// Frame loop
// ---------------------------------------------------------------------------

const others: Vector3[] = [];
const chest = new Vector3();
const labelAnchor = new Vector3();
const projected = new Vector3();
const screenPoint = { x: 0, y: 0 };
let lastMenuFrame = 0;
let lastFrame = performance.now();

/** When training began (0 = not training), its frames so far, and whether the arena is still loading. */
let trainingSince = 0;
let trainingFrames = 0;
let trainingLoading = true;

function frame(now: number): void {
  const dt = Math.min(0.1, Math.max(0, (now - lastFrame) / 1000));
  lastFrame = now;
  if (session) useArena(session.state.arenaId);
  virgilioAim.update();
  devilAim.update();

  // Render behind the presentation cards so shaders, sprites and the camera
  // are ready on the first visible countdown frame, including on slow phones.
  // The register open over the museum hides the world, like the home screen.
  const menuOnly = !session || (session.state.phase===MatchPhase.Lobby && lobby.open);
  if(menuOnly){
    if(!session)gameAudio.mixer.setMuseumMusic(false);
    // Back in the menus: the next training starts with its loading card again.
    trainingSince=0;trainingLoading=true;matchIntro.loading(false);
    gameAudio.update(session?.state??null,session?.localPredicted??null,session?.sessionId,{x:1,z:0},false);
    museumHud.update(null);
    // DOM menus need no Babylon render, world animation, label projection or post-processing.
    if(now-lastMenuFrame>=1000/30){
      lastMenuFrame=now;
      controls.sample();
      effects.update(1/30,project);
      moveEffects.update(1/30);
      if(session){
        const due=session.predict.tick(now);
        for(let i=0;i<due;i++){
          session.input.data.mx=session.input.data.mz=session.input.data.buttons=0;session.input.send();
        }
        matchHud.update(matchView(session,session.localPredicted));
      }
      lobby.update(session?lobbyView(session):null);
      matchIntro.update(session?.state??null,session?.sessionId,arena.displayName);
    }
    requestAnimationFrame(frame);return;
  }

  // Entering training draws the arena for the first time: shaders and textures
  // take a moment. The map card covers it until the scene is really ready. The
  // museum has no card: the arena's title belongs to the arena (D49).
  const trainee = session?.localPredicted;
  if (session?.state.phase === MatchPhase.Lobby && trainee && !inMuseum(trainee)) {
    if (trainingSince === 0) { trainingSince = now; trainingFrames = 0; }
    trainingFrames++;
    if (trainingLoading) trainingLoading = !(trainingFrames > 20 && now - trainingSince > 800 && renderer.scene.isReady());
  } else {
    trainingSince = 0;
    trainingLoading = true;
  }
  matchIntro.loading(trainingSince !== 0 && trainingLoading);

  let focus: Vector3 | null = null;
  others.length = 0;

  if (session) {
    // 1) Send exactly the fixed steps that are due; prediction runs on each send.
    const due = session.predict.tick(now);
    for (let i = 0; i < due; i++) {
      const sample = controls.sample();
      // Reading a statue's card or typing in the chat: the fighter stands still.
      if (session.state.phase === MatchPhase.Lobby && museumHud.busy) { sample.sx = sample.sy = sample.buttons = 0; }
      // An aimed S2 just confirmed holds its direction on the stick until it starts.
      const dir = devilAim.steer(session.localPredicted) ?? rig.stickToWorld(sample.sx, sample.sy);
      session.input.data.mx = quantizeAxis(dir.x);
      session.input.data.mz = quantizeAxis(dir.z);
      session.input.data.buttons = sample.buttons;
      // Charmed by Cleopatra: the same walk to her as on the server, or the prediction would fight it.
      const own = session.state.players.get(session.sessionId);
      if (own && own.charmTicks > 0) {
        const step = charmedCommand(session.localPredicted ?? own, session.state.players.get(own.charmer) ?? null);
        session.input.data.mx = step.mx;
        session.input.data.mz = step.mz;
        session.input.data.buttons = 0;
      }
      // Giovanna's voices: the rush starts toward the nearest enemy, the same rule as her bots (D47).
      const ready = session.localPredicted;
      if (own && own.characterId === "giovanna" && ready && (session.input.data.buttons & Button.Special2) !== 0 && ready.cooldown2 === 0 && ready.action === Action.None) {
        const rush = kitOf(own.characterId, own.metamorphTicks).table[Action.Special2]!;
        const drawn = [...fighters.values()].map((f) => ({ state: f.state, position: f.view.position, isLocal: f.isLocal }));
        const toward = voicesCommand(ready, enemies(drawn, own.team, rush));
        if (toward) { session.input.data.mx = toward.mx; session.input.data.mz = toward.mz; }
      }
      const selected=virgilioAim.take();
      session.input.data.target=selected?1:0;
      session.input.data.targetX=selected?.x??0;
      session.input.data.targetZ=selected?.z??0;
      session.input.send();
    }

    // 2) Draw everyone at their predicted (local) or interpolated (remote) pose.
    //    Combat state: predicted for the local fighter, latest received for the others.
    const predict = session.predict;
    const local = session.localPredicted;
    const frozen = session.state.phase === MatchPhase.Intro || session.state.phase === MatchPhase.Countdown || session.state.phase === MatchPhase.MapIntro;
    const localTeam = session.state.players.get(session.sessionId)?.team ?? 0;
    const cloneOwners=new Set([...session.state.clones.values()].map(copy=>copy.owner));
    for (const [fighterId,fighter] of fighters) {
      const p = fighter.state;
      const c = fighter.isLocal && local ? local : p;
      const duplicated=cloneOwners.has(fighterId);
      // Draw the authoritative shuffle at once; prediction/interpolation would
      // otherwise show the real fighter travelling from the old centre slot.
      const blend=drawnCloneOwners.has(fighterId)?1-Math.exp(-dt*16):1;
      const position=(key:'x'|'y'|'z')=>duplicated && !fighter.isLocal
        ? fighter.view.position[key]+(p[key]-fighter.view.position[key])*blend
        : predict.value(p,key);
      fighter.view.setChampion(championOf(p.characterId), kitOf(p.characterId,p.metamorphTicks));
      fighter.view.setTeam(p.team);
      fighter.view.update(
        {
          x: position("x"),
          y: position("y"),
          z: position("z"),
          fx: predict.value(p, "fx"),
          fz: predict.value(p, "fz"),
          vy: c.vy,
          grounded: c.grounded,
          dashing: c.dashTicks > 0,
          connected: p.connected,
          action: duplicated && c.action===Action.SuperSlam ? Action.None : c.action,
          actionTick: c.actionTick,
          hitstun: c.hitstun,anchorTicks:c.anchorTicks,
          slamReady: c.slamReady,
          shielded: p.shielded,
          frozen,
          knockedOut: c.knockedOut,
          grabbed: c.grabbed,
          poisoned: p.poisonTicks > 0,
          charmed: p.charmTicks > 0,
          spinning: c.spinTicks > 0,
        },
        dt,
      );
      fighter.health.update(p,session.state.settings.gameMode === GameMode.KO);
      statusMarks(fighter.view.label, p.charmTicks > 0 && !p.knockedOut, p.poisonTicks > 0 && !p.knockedOut);
      if (fighter.isLocal) focus = fighter.view.position;
      // The camera frames the enemies; allies may fall out of view (GDD §4.2).
      else if (!p.knockedOut && !areAllies(localTeam, p.team)) others.push(fighter.view.position);
    }
    // Eliminated in Arena KO: the camera follows whoever the spectator watches.
    const me = session.state.players.get(session.sessionId);
    const watched = spectator.update({
      out: session.state.settings.gameMode === GameMode.KO && session.state.phase === MatchPhase.Playing && (me?.knockedOut ?? false),
      teams: session.state.settings.teams === 1,
      standing: [...fighters].filter(([, f]) => !f.state.knockedOut).map(([id, f]) => ({ id, name: f.state.name })),
    });
    const followed = watched ? fighters.get(watched) : undefined;
    if (followed) focus = followed.view.position;
    for(const [id,copy]of session.state.clones){
      const p=copy.actor;
      let view=cloneViews.get(id);
      const isNew=!view;
      if(!view){
        const View=look2d?SpriteFighter:PlayerView;
        view=new View(renderer.scene,arena,p.name,p.color,false,labels,championOf(p.characterId),kitOf(p.characterId));
        cloneViews.set(id,view);
        cloneHealthBars.set(id,new HealthBar(view.label));
      }
      const blend=isNew || Vector3.DistanceSquared(view.position,new Vector3(p.x,p.y,p.z))>16?1:1-Math.exp(-dt*16);
      view.setTeam(p.team);
      cloneHealthBars.get(id)?.update(p,session.state.settings.gameMode===GameMode.KO);
      view.update({x:view.position.x+(p.x-view.position.x)*blend,y:view.position.y+(p.y-view.position.y)*blend,z:view.position.z+(p.z-view.position.z)*blend,
        fx:p.fx,fz:p.fz,vy:p.vy,grounded:p.grounded,dashing:p.dashTicks>0,connected:true,
        action:p.action===Action.SuperSlam?Action.None:p.action,actionTick:p.actionTick,hitstun:0,slamReady:session.state.players.get(copy.owner)?.slamReady??false,shielded:session.state.players.get(copy.owner)?.shielded??false,frozen:false,knockedOut:false,grabbed:false},dt);
      if(copy.owner!==session.sessionId && !areAllies(localTeam,p.team))others.push(view.position);
    }
    drawnCloneOwners.clear();for(const owner of cloneOwners)drawnCloneOwners.add(owner);
    for(const [id,view]of cloneViews)if(!session.state.clones.has(id)){view.dispose();cloneViews.delete(id);cloneHealthBars.delete(id);}
    for(const [id,c]of session.state.creatures){
      const p=c.actor,spec=summonOf(c.kind);if(!spec)continue;
      let item=creatureViews.get(id);const isNew=!item;
      if(!item){const View=look2d?SpriteFighter:PlayerView;const view=new View(renderer.scene,arena,spec.manifest.displayName,p.color,false,labels,spec.manifest,spec.kit);item={view,health:new HealthBar(view.label)};creatureViews.set(id,item);}
      const view=item.view,blend=isNew || Vector3.DistanceSquared(view.position,new Vector3(p.x,p.y,p.z))>16?1:1-Math.exp(-dt*18);
      view.setTeam(p.team);view.update({x:view.position.x+(p.x-view.position.x)*blend,y:view.position.y+(p.y-view.position.y)*blend,z:view.position.z+(p.z-view.position.z)*blend,fx:p.fx,fz:p.fz,vy:p.vy,grounded:p.grounded,dashing:false,connected:true,action:p.action,actionTick:p.actionTick,hitstun:p.hitstun,anchorTicks:p.anchorTicks,slamReady:false,shielded:false,frozen:false,knockedOut:p.knockedOut},dt);
      item.health.update(p,!p.knockedOut);
    }
    for(const [id,c]of creatureViews)if(!session.state.creatures.has(id)){c.view.dispose();creatureViews.delete(id);}
    for(const [id,t]of session.state.tentacles){let view=tentacleViews.get(id);if(!view){view=new TentacleView(renderer.scene);tentacleViews.set(id,view);}view.update(t,dt);}
    for(const [id,t]of tentacleViews)if(!session.state.tentacles.has(id)){t.dispose();tentacleViews.delete(id);}
    mussoliniScene.update(session.state,dt);
    barricadesView.update(session.state,dt);
    inferno?.updateTerrain(session.state.terrain);
    const drawn=[...fighters.values()].map(f=>({state:f.state,position:f.view.position,isLocal:f.isLocal}));
    pistolVisuals.update(look2d?local:null,session.state.players.get(session.sessionId)?.characterId??"",localTeam,
      session.liveArena(),drawn,dt);
    updateAimGuide(local,drawn,localTeam,frozen,dt);
    for (const pickup of pickups.values()) pickup.update(dt);
    for (const [id, zone] of session.state.zones) {
      if(zone.kind==='swarm'){
        let view=swarmZones.get(id);if(!view){view=new SwarmZoneView(renderer.scene);swarmZones.set(id,view);}view.update(zone,dt,session.liveArena());
      }
      if (zone.kind === "milk") {
        let bath = milkBaths.get(id);
        if (!bath) { bath = new MilkBathView(renderer.scene); milkBaths.set(id, bath); }
        bath.update(zone, dt);
      }
      if (zone.kind === "brioche") {
        let mine = briocheMines.get(id);
        if (!mine) { mine = new BriocheMineView(renderer.scene); briocheMines.set(id, mine); }
        mine.update(zone, dt);
      }
      if (zone.kind === "banner") {
        let banner = banners.get(id);
        if (!banner) { banner = new BannerView(renderer.scene); banners.set(id, banner); }
        banner.update(zone, dt);
      }
      if (zone.kind !== "salt") continue;
      let view = saltZones.get(id);
      if (!view) { view = new SaltZoneView(renderer.scene); saltZones.set(id,view); }
      view.update(zone,dt);
    }
    for (const [id, view] of saltZones) {
      if (session.state.zones.get(id)?.kind === "salt") continue;
      view.dispose(); saltZones.delete(id);
    }
    for(const [id,view]of swarmZones)if(session.state.zones.get(id)?.kind!=='swarm'){view.dispose();swarmZones.delete(id);}
    for (const [id, bath] of milkBaths) if (session.state.zones.get(id)?.kind !== "milk") { bath.dispose(); milkBaths.delete(id); }
    for (const [id, mine] of briocheMines) if (session.state.zones.get(id)?.kind !== "brioche") { mine.dispose(); briocheMines.delete(id); }
    for (const [id, banner] of banners) if (session.state.zones.get(id)?.kind !== "banner") { banner.dispose(); banners.delete(id); }
    for(const [id,f]of fighters){
      const p=f.isLocal?local:f.state;
      if(!p || p.anchorTicks===0 || p.knockedOut){anchorViews.get(id)?.dispose();anchorViews.delete(id);continue;}
      let view=anchorViews.get(id);if(!view){view=new AnchorView(renderer.scene);anchorViews.set(id,view);}
      view.update(f.view.position,p.anchorTicks,dt);
    }
    for(const [id,view]of anchorViews)if(!fighters.has(id)){view.dispose();anchorViews.delete(id);}
    for (const fighter of fighters.values()) updateBuffRing(fighter, now);
    crowdMeter.hidden = !colosseo;
    crowdFill.style.width = `${Math.round((session.state.crowd / CROWD.max) * 100)}%`;
    for (const { state, view } of projectiles.values()) {
      view.update(predict.value(state, "x"), predict.value(state, "y"), predict.value(state, "z"), state.fx, state.fz, dt);
    }
    if (local) {
      // The champion comes from the replicated state (the predicted copy only mirrors the simulation fields).
      const characterId = session.state.players.get(session.sessionId)?.characterId ?? "";
      predictLocalHits(local, characterId, localTeam, now);
      const moves = kitOf(characterId,session.state.players.get(session.sessionId)?.metamorphTicks).moves;
      abilities.update([abilitySlot(moves.special1, local.cooldown1), abilitySlot(moves.special2, local.cooldown2)]);
    }
    matchHud.update(matchView(session, local));
  }
  lobby.update(session ? lobbyView(session) : null);
  matchIntro.update(session?.state ?? null,session?.sessionId,arena.displayName);
  grabButton.update((session?.state.players.get(session.sessionId)?.carrying ?? 0) > 0);

  // Training: in the lobby, in the arena (the museum's "Allenamento", D49).
  const training = session?.state.phase === MatchPhase.Lobby && focus !== null && !inMuseum(focus);
  overviewButton.hidden = !training;
  if(!training){
    rig.overview=false;overviewButton.setAttribute('aria-pressed','false');overviewButton.textContent='Panoramica';
  }
  rig.update(dt, focus, others, controls.steering, (session?.state.bombs.size ?? 0)>0);
  renderer.camera.getDirectionToRef(Vector3.Right(),audioRight);
  gameAudio.update(session?.state??null,session?.localPredicted??null,session?.sessionId,audioRight,training);
  virgilioAim.updateRange();
  devilAim.updatePath();
  if (focus) chest.set(focus.x, focus.y + 1, focus.z);
  arenaView.updateOcclusion(renderer.camera.position, focus ? chest : null, dt);
  colosseo?.update(dt, session?.state.trap ?? 0, (session?.state.crowd ?? 0) / CROWD.max, renderer.camera.position);
  inferno?.update(dt, renderer.camera.position, focus ? chest : null);
  updateMuseum(dt, focus);
  propsView?.updateOcclusion(renderer.camera.position, focus ? chest : null, dt);
  if (propsView && session) {
    const props = session.state.props;
    propsView.update(dt, props);
    // A broken brazier's fire goes out, and comes back with it.
    fireIds.forEach((id, i) => hd2d?.setTorchLit(i, props.get(id)?.status !== PropStatus.Gone));
  }
  hd2d?.setMuseum(inMuseum(renderer.camera.position));
  hd2d?.update(dt, focus ? chest : null);
  renderer.render();
  placeLabels();
  effects.update(dt, project);
  moveEffects.update(dt);
  kafkaWater.update(dt);
  // Lasting champion effects (Auramennone's aura), each with its fighters on the field.
  for (const visuals of allVisuals()) {
    if (!visuals.frame) continue;
    const own = new Map<string, DrawnFighter>();
    for (const [id, f] of fighters) if (f.state.characterId === visuals.id) own.set(id, f);
    visuals.frame(championFx, own, dt);
  }
  for (let i = summons.length - 1; i >= 0; i--) {
    const summon = summons[i]!;
    summon.age += dt;
    if (summon.age > 1.8 || !session) { summon.view.dispose(); summons.splice(i, 1); }
    else summon.view.update(summon.x - summon.fx * 0.8, summon.y, summon.z - summon.fz * 0.8, summon.fx, summon.fz, dt);
  }
  for (let i=flourishes.length-1;i>=0;i--) {
    const p=flourishes[i]!;
    p.age+=dt;
    if(p.age>0.62 || !session){p.view.dispose();flourishes.splice(i,1);}
    else p.view.update(p.x,p.y,p.z,p.fx,p.fz,dt);
  }

  hud.update({
    fps: renderer.engine.getFps(),
    rttMs: session ? session.rtt : null,
    renderWidth: renderer.renderSize.width,
    renderHeight: renderer.renderSize.height,
    players: fighters.size,
    inFlight: session ? session.inputsInFlight : 0,
    meshes: renderer.scene.getActiveMeshes().length,
  });

  requestAnimationFrame(frame);
}

function updateBuffRing(fighter: Fighter, now: number): void {
  let ring = buffRings.get(fighter.id);
  const strong = fighter.state.buff === Buff.Strength;
  if (!strong) {
    if (ring) ring.isVisible = false;
    return;
  }
  if (!ring) {
    ring = CreateDisc("buff", { radius: 0.55, tessellation: 20 }, renderer.scene);
    ring.rotation.x = Math.PI / 2;
    ring.material = buffMaterial;
    ring.isPickable = false;
    buffRings.set(fighter.id, ring);
  }
  ring.isVisible = true;
  const at = fighter.view.position;
  ring.position.set(at.x, at.y + 0.02, at.z);
  const pulse = 1 + 0.12 * Math.sin(now / 90);
  ring.scaling.set(pulse, pulse, 1);
}

/**
 * The arrow on the floor: along the stick while it is pushed (the next move,
 * chained ones too, turns that way), else along the facing. Mussolini's sight
 * on the enemy his pistol would lock onto from there.
 */
function updateAimGuide(local: PlayerState | null, drawn: Parameters<typeof enemies>[0], team: number, frozen: boolean, dt: number): void {
  const own = session?.state.players.get(session.sessionId);
  const view = session ? fighters.get(session.sessionId)?.view : undefined;
  const phase = session?.state.phase;
  // A ghost (no champion yet, D49) has nothing to aim.
  const acting = local && own && view && own.characterId !== "" && !local.knockedOut && !frozen &&
    (phase === MatchPhase.Playing || phase === MatchPhase.Lobby);
  const ground = acting ? groundHeightAt(world, view.position.x, view.position.z, view.position.y + 0.05) : null;
  if (!acting || ground === null) {
    aimGuide.update(null, { x: 0, z: 1 }, false, null, dt);
    return;
  }
  const mx = session!.input.data.mx;
  const mz = session!.input.data.mz;
  const push = Math.hypot(mx, mz);
  const steering = push > Movement.deadzone * AXIS_MAX;
  const aim = steering ? { x: mx / push, z: mz / push } : { x: local.fx, z: local.fz };
  guideAim = aim;
  const shot = kitOf(own.characterId, own.metamorphTicks).table.find((d) => d?.projectile?.aimAssistDeg);
  const target = shot ? aimTarget({ x: local.x, y: local.y, z: local.z, fx: aim.x, fz: aim.z }, shot.projectile!, enemies(drawn, team, shot)) : null;
  const lock = target ? { x: target.x, y: groundHeightAt(arena, target.x, target.z, target.y + 0.05) ?? target.y, z: target.z } : null;
  aimGuide.update({ x: view.position.x, y: ground, z: view.position.z }, aim, steering, lock, dt);
}

function matchView(s: GameSession, local: PlayerState | null): Parameters<MatchHud["update"]>[0] {
  const state = s.state;
  const players: ScoreRow[] = [];
  state.players.forEach((p, id) => {
    players.push({
      id,
      characterId: p.characterId,
      name: p.name,
      color: p.color,
      score: p.score,
      slam: p.slam / DUEL_RULES.slamMax,
      slamReady: p.slamReady,
      connected: p.connected,
      team: p.team,
      hp: p.hp,
      maxHp: p.maxHp,
      respawnTicks: p.respawnTicks,
      metamorphTicks:p.metamorphTicks,
      out: state.settings.gameMode === GameMode.KO && state.phase === MatchPhase.Playing && p.knockedOut,
    });
  });
  const results: ResultRow[] = state.results.map((r) => ({
    id: r.sessionId,
    name: r.name,
    color: r.color,
    score: r.score,
    left: r.left,
    champion: championOf(r.characterId).displayName,
    team: r.team,
    standing: r.standing,
  }));
  return {
    phase: state.phase,
    timeLeft: state.timeLeft,
    localId: s.sessionId,
    localSlam: (state.players.get(s.sessionId)?.slam ?? 0) / DUEL_RULES.slamMax,
    localReady: local?.slamReady ?? false,
    players,
    results,
    winner: state.winner,
    endReason: state.endReason,
    teams: state.settings.teams === 1 || state.winnerTeam !== 0 || state.results.some((r) => r.team !== 0),
    teamScores: [state.teamScoreBlue, state.teamScoreRed],
    goal: state.goal,
    winnerTeam: state.winnerTeam,
    koMode: state.settings.gameMode === GameMode.KO,
    training: state.phase===MatchPhase.Lobby && local !== null && !inMuseum(local),
    overtime: state.overtime,
  };
}

function lobbyView(s: GameSession): LobbyView {
  const state = s.state;
  const players: LobbyView["players"] = [];
  state.players.forEach((p, id) => {
    players.push({ id, name: p.name, color: p.color, ready: p.ready, connected: p.connected, characterId: p.characterId, team: p.team, bot: p.bot });
  });
  const winner = state.results.find((r) => r.sessionId === state.winner);
  const teamMatch = state.results.some((r) => r.team !== 0);
  const lastMatch =
    state.results.length === 0
      ? ""
      : teamMatch
        ? `Ultima partita: ${state.winnerTeam ? `hanno vinto i ${TEAM_NAMES[state.winnerTeam]}` : "pareggio"} (${state.teamScoreBlue}–${state.teamScoreRed})`
        : `Ultima partita: ${winner ? `ha vinto ${winner.name}` : "pareggio"} (${state.results.map((r) => r.score).join("–")})`;
  return {
    phase: state.phase,
    code: state.code,
    arenaId: state.arenaId,
    localId: s.sessionId,
    hostId: state.hostId,
    players,
    settings: {
      maxPlayers: state.settings.maxPlayers,
      targetScore: state.settings.targetScore,
      durationSec: state.settings.durationSec,
      teams: state.settings.teams,
      gameMode: state.settings.gameMode,
    },
    lastMatch,
  };
}

/** World point → CSS pixels over the canvas; false when behind the camera. */
function project(world: Vector3, out: { x: number; y: number }): boolean {
  const { width, height } = renderer.renderSize;
  const viewport = renderer.camera.viewport.toGlobal(width, height);
  Vector3.ProjectToRef(world, Matrix.IdentityReadOnly, renderer.scene.getTransformMatrix(), viewport, projected);
  if (!(projected.z > 0 && projected.z < 1)) return false;
  out.x = projected.x * (canvas.clientWidth / width);
  out.y = projected.y * (canvas.clientHeight / height);
  return true;
}

/** The museum (D49): statues of the champions nobody has, plaques, the usable object and its controls. */
function updateMuseum(dt: number, focus: Vector3 | null): void {
  const own = session?.state.players.get(session.sessionId);
  const shown = session !== null && own !== undefined && session.state.phase === MatchPhase.Lobby && !lobby.open;
  const holders = new Map<string, string>();
  session?.state.players.forEach((p) => {
    if (p.characterId) holders.set(p.characterId, p.name);
  });
  const here = shown && focus !== null && inMuseum(focus);
  gameAudio.mixer.setMuseumMusic(
    session?.state.phase === MatchPhase.Lobby && own !== undefined && focus !== null && inMuseum(focus),
  );
  museumProfile.hidden = !here;
  if (here) {
    const profile = googleAuth?.profile;
    const level = profile ? `Liv. ${profile.level}` : "";
    const avatar = profile?.avatar || null;
    if (museumProfileName.textContent !== own.name) {
      museumProfileName.textContent = own.name;
      museumProfileInitial.textContent = Array.from(own.name)[0]?.toLocaleUpperCase("it") ?? "";
    }
    if (museumProfileLevel.textContent !== level) {
      museumProfileLevel.textContent = level;
      museumProfileLevel.hidden = !level;
    }
    if (museumProfilePhoto.getAttribute("src") !== avatar) {
      museumProfilePhoto.hidden = !avatar;
      if (avatar) museumProfilePhoto.src = avatar;
      else museumProfilePhoto.removeAttribute("src");
    }
  }
  const usable = here && focus ? museumObjectAt(focus, MUSEUM_CHAMPIONS.length) : null;
  museumView.update(dt, renderer.camera, holders, usable);
  museumHud.update(shown && session ? {
    inArena: !here,
    ghost: own.characterId === "",
    isHost: session.isHost,
    allPicked: [...session.state.players.values()].every((p) => p.ready),
    players: session.state.players.size,
    usable,
    mine: own.characterId,
    holders,
  } : null);
}

/** A line of chat (D49): in the log, and for a few seconds over its writer's head. */
function heardChat(line: ChatLine): void {
  const p = session?.state.players.get(line.from);
  if (!p) return;
  museumHud.heard(p.name, fighterCss(p.color, p.team), line.text);
  const label = fighters.get(line.from)?.view.label;
  if (!label) return;
  label.querySelector(".say")?.remove();
  const say = document.createElement("span");
  say.className = "say";
  say.textContent = line.text;
  label.append(say);
  window.setTimeout(() => say.remove(), 5000);
}

/** Name tags are HTML, so they stay crisp over the pixelated scene. */
function placeLabels(): void {
  museumView.placeLabels(project, [...fighters.values()].map((f) => ({ x: f.view.position.x, y: f.view.position.y, z: f.view.position.z, height: f.view.labelHeight })));
  for (const view of [...fighters.values()].map(f=>f.view).concat([...cloneViews.values()],[...creatureViews.values()].map(c=>c.view))) {
    labelAnchor.copyFrom(view.position);
    labelAnchor.y += view.labelHeight;
    const visible = project(labelAnchor, screenPoint);
    view.label.style.visibility = visible ? "visible" : "hidden";
    if (visible) {
      view.label.style.transform = `translate(-50%, -100%) translate(${screenPoint.x.toFixed(1)}px, ${screenPoint.y.toFixed(1)}px)`;
    }
  }
}

/** Keep the screen awake while playing (best effort; needs a user gesture on some browsers). */
function keepScreenAwake(): void {
  const request = (): void => {
    navigator.wakeLock?.request("screen").catch(() => {});
  };
  document.addEventListener("pointerdown", request, { once: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") request();
  });
}

function showUpdating(): void {
  hud.showStatus("Arriva la nuova versione…");
}

keepScreenAwake();
requestAnimationFrame(frame);
void boot();
// A new version reloads the page, but never in the middle of a match.
watchForUpdates(() => !session || session.state.phase === MatchPhase.Lobby, showUpdating);
